import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { CalendarDays, Mail, DollarSign, LogOut, Ban, ArrowLeftRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";

const PORTALS = {
  "punto-nima": { property: "condo-507", name: "Punto Nima · San José del Cabo" },
  "casa-de-leon": { property: "casa-de-leon", name: "Casa de León · Punta Arena" },
};

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const { portal } = useParams();
  const cfg = PORTALS[portal] || PORTALS["punto-nima"];
  const property = cfg.property;
  const [tab, setTab] = useState("bookings");
  const [bookings, setBookings] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [settings, setSettings] = useState(null);
  const [newDate, setNewDate] = useState("");
  const [guide, setGuide] = useState(null);
  const [reviews, setReviews] = useState(null);
  const [seasons, setSeasons] = useState(null);
  const [curve, setCurve] = useState(null);
  const [icsUrl, setIcsUrl] = useState("");
  const [members, setMembers] = useState([]);

  useEffect(() => { if (user === false) nav("/admin/login"); }, [user, nav]);

  const load = async () => {
    try {
      const P = { params: { property } };
      const [b, c, s, g, rv, se, rc, ci, pc] = await Promise.all([
        api.get("/admin/bookings", P), api.get("/admin/contacts", P), api.get("/admin/settings", P), api.get("/admin/guide", P), api.get("/admin/reviews", P), api.get("/admin/seasons", P), api.get("/admin/ratecurve", P), api.get("/admin/calendar-info", P), api.get("/admin/private-collection", P),
      ]);
      setBookings(b.data); setContacts(c.data); setSettings(s.data); setGuide(g.data); setReviews(rv.data); setSeasons(se.data); setCurve(rc.data); setMembers(pc.data);
      setIcsUrl(`${process.env.REACT_APP_BACKEND_URL}${ci.data.ics_path}`);
    } catch { nav("/admin/login"); }
  };
  useEffect(() => { if (user && user.email) load(); }, [user, property]);

  const savePricing = async () => {
    try {
      await api.put("/admin/pricing", {
        nightly_rate: Number(settings.nightly_rate), cleaning_fee: Number(settings.cleaning_fee),
        damage_deposit: Number(settings.damage_deposit) || 0,
        service_fee_pct: Number(settings.service_fee_pct), min_nights: Number(settings.min_nights),
        max_guests: Number(settings.max_guests),
        weekly_discount_pct: Number(settings.weekly_discount_pct) || 0,
        monthly_discount_pct: Number(settings.monthly_discount_pct) || 0,
        last_minute_discount_pct: Number(settings.last_minute_discount_pct) || 0,
        last_minute_window_days: Number(settings.last_minute_window_days) || 0,
        dynamic_pricing: !!settings.dynamic_pricing,
      }, { params: { property } });
      toast.success("Pricing updated");
    } catch { toast.error("Update failed"); }
  };

  const addBlackout = async () => {
    if (!newDate) return;
    const dates = [...(settings.blackout_dates || []), newDate];
    await api.put("/admin/blackout", { dates }, { params: { property } });
    setSettings({ ...settings, blackout_dates: [...new Set(dates)].sort() });
    setNewDate("");
    toast.success("Date blocked");
  };
  const removeBlackout = async (d) => {
    const dates = settings.blackout_dates.filter((x) => x !== d);
    await api.put("/admin/blackout", { dates }, { params: { property } });
    setSettings({ ...settings, blackout_dates: dates });
  };

  const saveGuide = async () => {
    try {
      await api.put("/admin/guide", { categories: guide }, { params: { property } });
      toast.success("Guide updated");
    } catch { toast.error("Update failed"); }
  };
  const setCatField = (ci, field, val) => setGuide(guide.map((c, i) => i === ci ? { ...c, [field]: val } : c));
  const setItemField = (ci, ii, field, val) => setGuide(guide.map((c, i) => i === ci ? { ...c, items: c.items.map((it, j) => j === ii ? { ...it, [field]: val } : it) } : c));
  const addItem = (ci) => setGuide(guide.map((c, i) => i === ci ? { ...c, items: [...c.items, { name: "", desc: "" }] } : c));
  const removeItem = (ci, ii) => setGuide(guide.map((c, i) => i === ci ? { ...c, items: c.items.filter((_, j) => j !== ii) } : c));

  const saveReviews = async () => {
    try {
      await api.put("/admin/reviews", { items: reviews.map((r) => ({ ...r, rating: Number(r.rating) || 5 })) }, { params: { property } });
      toast.success("Reviews updated");
    } catch { toast.error("Update failed"); }
  };
  const setReviewField = (i, field, val) => setReviews(reviews.map((r, j) => j === i ? { ...r, [field]: val } : r));
  const addReview = () => setReviews([...(reviews || []), { name: "", location: "", rating: 5, text: "", date: "" }]);
  const removeReview = (i) => setReviews(reviews.filter((_, j) => j !== i));

  const todayISO = new Date().toISOString().slice(0, 10);
  const markRefunded = async (sessionId) => {
    if (!window.confirm("Issue the refundable damage deposit back to the guest via Stripe now? This cannot be undone.")) return;
    try {
      const { data } = await api.put(`/admin/bookings/${sessionId}/deposit-refunded`);
      setBookings(bookings.map((b) => b.session_id === sessionId ? { ...b, deposit_refunded: true } : b));
      toast.success(data.status === "already_refunded" ? "Already refunded" : `Deposit refunded to guest · confirmation email sent`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const depositDue = (b) => b.payment_status === "paid" && (b.damage_deposit || 0) > 0 && !b.deposit_refunded && b.check_out < todayISO;

  const saveSeasons = async () => {
    try {
      const clean = seasons.filter((s) => s.name && s.start && s.end).map((s) => ({ ...s, nightly_rate: Number(s.nightly_rate) || 0, min_nights: Number(s.min_nights) || 0 }));
      await api.put("/admin/seasons", { seasons: clean }, { params: { property } });
      setSeasons(clean);
      toast.success("Seasonal rates updated");
    } catch { toast.error("Update failed"); }
  };
  const setSeasonField = (i, field, val) => setSeasons(seasons.map((s, j) => j === i ? { ...s, [field]: val } : s));
  const addSeason = () => setSeasons([...(seasons || []), { name: "", start: "", end: "", nightly_rate: settings?.nightly_rate || 0, min_nights: 5 }]);
  const removeSeason = (i) => setSeasons(seasons.filter((_, j) => j !== i));

  const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const saveCurve = async () => {
    try {
      await api.put("/admin/ratecurve", { month_factors: curve.month_factors.map((x) => Number(x) || 0), holiday_premiums: curve.holiday_premiums }, { params: { property } });
      toast.success("Rate curve saved");
    } catch { toast.error("Save failed"); }
  };
  const resetCurve = async () => {
    try { const { data } = await api.post("/admin/ratecurve/reset", null, { params: { property } }); setCurve({ ...curve, ...data }); toast.success("Reset to Los Cabos market baseline"); }
    catch { toast.error("Reset failed"); }
  };
  const setMonthFactor = (i, val) => setCurve({ ...curve, month_factors: curve.month_factors.map((f, j) => j === i ? val : f) });
  const setHolidayField = (i, field, val) => setCurve({ ...curve, holiday_premiums: curve.holiday_premiums.map((h, j) => j === i ? { ...h, [field]: val } : h) });
  const addHoliday = () => setCurve({ ...curve, holiday_premiums: [...(curve.holiday_premiums || []), { name: "", from: "", to: "", factor: 1.5 }] });
  const removeHoliday = (i) => setCurve({ ...curve, holiday_premiums: curve.holiday_premiums.filter((_, j) => j !== i) });

  if (!user || !settings) return <div className="min-h-screen bg-[#F6F4F0]" />;

  const paid = bookings.filter((b) => b.payment_status === "paid");
  const revenue = paid.reduce((s, b) => s + (b.amount || 0), 0);
  const stats = [
    ["Confirmed Bookings", paid.length, CalendarDays],
    ["Total Revenue", `$${revenue.toFixed(0)}`, DollarSign],
    ["Inquiries", contacts.length, Mail],
    ["Blocked Dates", (settings.blackout_dates || []).length, Ban],
  ];
  const tabs = [["bookings", "Bookings"], ["inquiries", "Inquiries"], ["collection", "Private Collection"], ["pricing", "Pricing"], ["curve", "Rate Curve"], ["seasons", "Seasonal Rates"], ["calendar", "Calendar"], ["guide", "Local Guide"], ["reviews", "Reviews"]];
  const cell = "px-4 py-3 text-sm";
  const inp = "bg-white border border-[#EAE5DC] rounded-sm px-3 py-2 font-light focus:outline-none focus:border-[#B86B52]";

  const exportMembersCsv = () => {
    const rows = [["Name", "Email", "Joined"], ...members.map((m) => [m.name || "", m.email || "", (m.created_at || "").slice(0, 10)])];
    const csv = rows.map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8;" }));
    const a = document.createElement("a");
    a.href = url; a.download = `private-collection-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-[#F6F4F0]" data-testid="admin-dashboard">
      <header className="bg-[#0F2537] text-[#F6F4F0] px-6 lg:px-10 py-5 flex items-center justify-between">
        <div>
          <span className="font-display text-xl" data-testid="portal-title">{cfg.name} · Owner Portal</span>
          <p className="text-[#A3A09A] text-xs mt-0.5">{user.email}</p>
        </div>
        <div className="flex items-center gap-6">
          <button data-testid="switch-property" onClick={() => nav("/admin")}
            className="flex items-center gap-2 text-sm text-[#A3A09A] hover:text-white transition-colors">
            <ArrowLeftRight className="w-4 h-4" /> Switch property
          </button>
          <button data-testid="logout-button" onClick={async () => { await logout(); nav("/admin/login"); }}
            className="flex items-center gap-2 text-sm text-[#A3A09A] hover:text-white transition-colors">
            <LogOut className="w-4 h-4" /> Logout
          </button>
        </div>
      </header>

      <div className="max-w-[1200px] mx-auto px-6 lg:px-10 py-10">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
          {stats.map(([label, val, Icon]) => (
            <div key={label} className="bg-white border border-[#EAE5DC] rounded-sm p-5">
              <Icon className="w-5 h-5 text-[#B86B52] mb-3" strokeWidth={1.5} />
              <div className="font-display text-3xl text-[#1A1918]">{val}</div>
              <div className="text-[#5C5A56] text-xs mt-1">{label}</div>
            </div>
          ))}
        </div>

        <div className="flex gap-2 mb-6 border-b border-[#EAE5DC]">
          {tabs.map(([id, label]) => (
            <button key={id} data-testid={`tab-${id}`} onClick={() => setTab(id)}
              className={`px-4 py-3 text-sm transition-colors ${tab === id ? "text-[#B86B52] border-b-2 border-[#B86B52]" : "text-[#5C5A56]"}`}>
              {label}
            </button>
          ))}
        </div>

        {tab === "bookings" && (
          <>
            {bookings.filter(depositDue).length > 0 && (
              <div className="mb-5 bg-[#B86B52]/10 border border-[#B86B52]/30 rounded-sm p-4" data-testid="deposit-reminder">
                <div className="text-[#B86B52] font-medium text-sm mb-1">
                  💸 {bookings.filter(depositDue).length} damage deposit{bookings.filter(depositDue).length > 1 ? "s" : ""} to refund
                </div>
                <p className="text-[#5C5A56] text-sm font-light">These guests have checked out. Click the refund button to issue the Stripe refund automatically and email the guest.</p>
              </div>
            )}
            <div className="bg-white border border-[#EAE5DC] rounded-sm overflow-x-auto" data-testid="bookings-table">
              <table className="w-full text-left">
                <thead className="text-[#5C5A56] text-xs uppercase tracking-wider border-b border-[#EAE5DC]">
                  <tr><th className={cell}>Guest</th><th className={cell}>Dates</th><th className={cell}>Guests</th><th className={cell}>Total</th><th className={cell}>Status</th><th className={cell}>Deposit</th></tr>
                </thead>
                <tbody>
                  {bookings.length === 0 && <tr><td className={`${cell} text-[#5C5A56]`} colSpan={6}>No bookings yet.</td></tr>}
                  {bookings.map((b, i) => (
                    <tr key={i} className="border-b border-[#EAE5DC] last:border-0">
                      <td className={cell}><div className="text-[#1A1918]">{b.guest_name}</div><div className="text-[#5C5A56] text-xs">{b.guest_email}</div></td>
                      <td className={`${cell} text-[#1A1918]`}>{b.check_in} → {b.check_out}</td>
                      <td className={`${cell} text-[#1A1918]`}>{b.guests}</td>
                      <td className={`${cell} text-[#1A1918]`}>${b.amount}</td>
                      <td className={cell}>
                        <span className={`px-2 py-1 rounded-sm text-xs ${b.payment_status === "paid" ? "bg-[#8C9B90]/30 text-[#2A4759]" : "bg-[#EAE5DC] text-[#5C5A56]"}`}>{b.payment_status}</span>
                      </td>
                      <td className={cell}>
                        {b.payment_status !== "paid" || !(b.damage_deposit > 0) ? (
                          <span className="text-[#5C5A56] text-xs">—</span>
                        ) : b.deposit_refunded ? (
                          <div className="text-xs">
                            <span className="text-[#2A4759]">Refunded ✓</span>
                            {b.deposit_refunded_at && <div className="text-[#5C5A56]">{b.deposit_refunded_at.slice(0, 10)}</div>}
                            {b.deposit_refund_id && <div className="text-[#5C5A56] font-mono text-[10px] break-all" title={b.deposit_refund_id}>{b.deposit_refund_id}</div>}
                          </div>
                        ) : depositDue(b) ? (
                          <button data-testid={`refund-${b.session_id}`} onClick={() => markRefunded(b.session_id)}
                            className="px-2.5 py-1 rounded-sm text-xs bg-[#B86B52] text-white hover:bg-[#a35c45] transition-colors">
                            Refund ${b.damage_deposit} now
                          </button>
                        ) : (
                          <span className="text-[#5C5A56] text-xs">Held ${b.damage_deposit}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {tab === "inquiries" && (
          <div className="space-y-4" data-testid="inquiries-list">
            {contacts.length === 0 && <p className="text-[#5C5A56]">No inquiries yet.</p>}
            {contacts.map((c, i) => (
              <div key={i} className="bg-white border border-[#EAE5DC] rounded-sm p-5">
                <div className="flex justify-between"><span className="text-[#1A1918] font-medium">{c.name}</span><span className="text-[#5C5A56] text-sm">{c.email} · {c.phone}</span></div>
                <p className="text-[#5C5A56] font-light mt-2">{c.message}</p>
              </div>
            ))}
          </div>
        )}

        {tab === "collection" && (
          <div data-testid="collection-list">
            <div className="flex items-center justify-between mb-5">
              <p className="text-[#5C5A56] text-sm">{members.length} member{members.length === 1 ? "" : "s"} on the Private Collection list</p>
              <button data-testid="export-members-csv" onClick={exportMembersCsv} disabled={members.length === 0}
                className="px-5 py-2.5 bg-[#2A4759] text-white text-sm rounded-sm hover:bg-[#1e3644] transition-colors disabled:opacity-50">
                Export CSV
              </button>
            </div>
            {members.length === 0 ? (
              <p className="text-[#5C5A56]">No one has joined yet.</p>
            ) : (
              <div className="bg-white border border-[#EAE5DC] rounded-sm overflow-x-auto" data-testid="members-table">
                <table className="w-full text-left">
                  <thead className="border-b border-[#EAE5DC] text-[#5C5A56]">
                    <tr><th className={cell}>Name</th><th className={cell}>Email</th><th className={cell}>Joined</th></tr>
                  </thead>
                  <tbody>
                    {members.map((m, i) => (
                      <tr key={i} className="border-b border-[#EAE5DC] last:border-0" data-testid={`member-row-${i}`}>
                        <td className={`${cell} text-[#1A1918]`}>{m.name}</td>
                        <td className={`${cell} text-[#5C5A56]`}>{m.email}</td>
                        <td className={`${cell} text-[#5C5A56]`}>{(m.created_at || "").slice(0, 10)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {tab === "pricing" && (
          <div className="bg-white border border-[#EAE5DC] rounded-sm p-8 max-w-md" data-testid="pricing-form">
            <label className="flex items-center gap-3 mb-6 cursor-pointer" data-testid="dynamic-pricing-toggle">
              <input type="checkbox" checked={!!settings.dynamic_pricing} onChange={(e) => setSettings({ ...settings, dynamic_pricing: e.target.checked })} className="w-4 h-4 accent-[#B86B52]" />
              <span className="text-[#1A1918] text-sm">Automatic seasonal (dynamic) pricing — rates follow Los Cabos market by date</span>
            </label>
            <div className="space-y-5">
              {[["Base Nightly Rate ($)", "nightly_rate"], ["Cleaning Fee ($)", "cleaning_fee"], ["Refundable Damage Deposit ($)", "damage_deposit"], ["Service Fee (0-1)", "service_fee_pct"], ["Weekly Discount (0-1, 7+ nights)", "weekly_discount_pct"], ["Monthly Discount (0-1, 28+ nights)", "monthly_discount_pct"], ["Last-Minute Discount (0-1)", "last_minute_discount_pct"], ["Last-Minute Window (days)", "last_minute_window_days"], ["Minimum Nights", "min_nights"], ["Max Guests", "max_guests"]].map(([label, key]) => (
                <div key={key}>
                  <label className="overline text-[#5C5A56] block mb-2">{label}</label>
                  <input type="number" step="any" className={`${inp} w-full`} value={settings[key]}
                    onChange={(e) => setSettings({ ...settings, [key]: e.target.value })} data-testid={`pricing-${key}`} />
                </div>
              ))}
              <button data-testid="save-pricing" onClick={savePricing} className="w-full py-3 bg-[#B86B52] text-white rounded-sm hover:bg-[#a35c45] transition-colors">Save Pricing</button>
            </div>
          </div>
        )}

        {tab === "calendar" && (
          <div className="max-w-md space-y-6">
            <div className="bg-white border border-[#EAE5DC] rounded-sm p-6" data-testid="ical-subscribe">
              <div className="overline text-[#B86B52] mb-2">Sync to Outlook / Google Calendar</div>
              <p className="text-[#5C5A56] font-light text-sm mb-4">Subscribe to this private link in Outlook (Add calendar → Subscribe from web) to see confirmed bookings & blocked dates appear automatically.</p>
              <div className="flex gap-2">
                <input readOnly value={icsUrl} data-testid="ical-url" onFocus={(e) => e.target.select()}
                  className={`${inp} w-full text-xs`} />
                <button data-testid="copy-ical" onClick={() => { navigator.clipboard.writeText(icsUrl); toast.success("Calendar link copied"); }}
                  className="px-4 bg-[#2A4759] text-white rounded-sm hover:bg-[#1e3644] transition-colors text-sm whitespace-nowrap">Copy</button>
              </div>
              <p className="text-[10px] text-[#5C5A56] mt-3">Keep this link private — anyone with it can view your booking dates.</p>
            </div>
            <div className="bg-white border border-[#EAE5DC] rounded-sm p-8" data-testid="blackout-manager">
            <p className="text-[#5C5A56] font-light mb-5">Block dates to prevent bookings (maintenance, personal use, etc.).</p>
            <div className="flex gap-3 mb-6">
              <input type="date" className={inp} value={newDate} onChange={(e) => setNewDate(e.target.value)} data-testid="blackout-date-input" />
              <button data-testid="add-blackout" onClick={addBlackout} className="px-5 bg-[#2A4759] text-white rounded-sm hover:bg-[#1e3644] transition-colors">Block</button>
            </div>
            <div className="flex flex-wrap gap-2">
              {(settings.blackout_dates || []).map((d) => (
                <span key={d} className="flex items-center gap-2 px-3 py-1.5 bg-[#EAE5DC] rounded-sm text-sm text-[#1A1918]">
                  {d}<button onClick={() => removeBlackout(d)} className="text-[#B86B52] hover:text-[#a35c45]">✕</button>
                </span>
              ))}
              {(settings.blackout_dates || []).length === 0 && <span className="text-[#5C5A56] text-sm">No blocked dates.</span>}
            </div>
          </div>
          </div>
        )}

        {tab === "guide" && guide && (
          <div className="space-y-6" data-testid="guide-editor">
            {guide.map((c, ci) => (
              <div key={ci} className="bg-white border border-[#EAE5DC] rounded-sm p-6">
                <div className="grid sm:grid-cols-2 gap-3 mb-5">
                  <div>
                    <label className="overline text-[#5C5A56] block mb-2">Tag</label>
                    <input className={`${inp} w-full`} value={c.tag} onChange={(e) => setCatField(ci, "tag", e.target.value)} data-testid={`guide-tag-${ci}`} />
                  </div>
                  <div>
                    <label className="overline text-[#5C5A56] block mb-2">Title</label>
                    <input className={`${inp} w-full`} value={c.title} onChange={(e) => setCatField(ci, "title", e.target.value)} data-testid={`guide-title-${ci}`} />
                  </div>
                </div>
                <div className="space-y-3">
                  {c.items.map((it, ii) => (
                    <div key={ii} className="flex gap-3 items-start">
                      <div className="flex-1 grid sm:grid-cols-2 gap-3">
                        <input className={`${inp} w-full`} placeholder="Name" value={it.name} onChange={(e) => setItemField(ci, ii, "name", e.target.value)} data-testid={`guide-item-name-${ci}-${ii}`} />
                        <input className={`${inp} w-full`} placeholder="Short description" value={it.desc} onChange={(e) => setItemField(ci, ii, "desc", e.target.value)} data-testid={`guide-item-desc-${ci}-${ii}`} />
                      </div>
                      <button onClick={() => removeItem(ci, ii)} className="text-[#B86B52] hover:text-[#a35c45] px-2 py-2" data-testid={`guide-remove-${ci}-${ii}`}>✕</button>
                    </div>
                  ))}
                </div>
                <button onClick={() => addItem(ci)} className="mt-4 text-sm text-[#2A4759] hover:text-[#B86B52] transition-colors" data-testid={`guide-add-${ci}`}>+ Add recommendation</button>
              </div>
            ))}
            <button data-testid="save-guide" onClick={saveGuide} className="px-8 py-3 bg-[#B86B52] text-white rounded-sm hover:bg-[#a35c45] transition-colors">Save Local Guide</button>
          </div>
        )}

        {tab === "reviews" && reviews && (
          <div className="space-y-5" data-testid="reviews-editor">
            {reviews.map((r, i) => (
              <div key={i} className="bg-white border border-[#EAE5DC] rounded-sm p-6">
                <div className="grid sm:grid-cols-3 gap-3 mb-3">
                  <input className={`${inp} w-full`} placeholder="Guest name" value={r.name} onChange={(e) => setReviewField(i, "name", e.target.value)} data-testid={`review-name-${i}`} />
                  <input className={`${inp} w-full`} placeholder="Location" value={r.location} onChange={(e) => setReviewField(i, "location", e.target.value)} data-testid={`review-location-${i}`} />
                  <div className="flex gap-3">
                    <input className={`${inp} w-full`} placeholder="Date (e.g. March 2026)" value={r.date} onChange={(e) => setReviewField(i, "date", e.target.value)} data-testid={`review-date-${i}`} />
                    <select className={inp} value={r.rating} onChange={(e) => setReviewField(i, "rating", e.target.value)} data-testid={`review-rating-${i}`}>
                      {[5, 4, 3, 2, 1].map((n) => <option key={n} value={n}>{n}★</option>)}
                    </select>
                  </div>
                </div>
                <div className="flex gap-3 items-start">
                  <textarea rows={2} className={`${inp} w-full resize-none`} placeholder="Review text" value={r.text} onChange={(e) => setReviewField(i, "text", e.target.value)} data-testid={`review-text-${i}`} />
                  <button onClick={() => removeReview(i)} className="text-[#B86B52] hover:text-[#a35c45] px-2 py-2" data-testid={`review-remove-${i}`}>✕</button>
                </div>
              </div>
            ))}
            <div className="flex gap-4">
              <button onClick={addReview} className="text-sm text-[#2A4759] hover:text-[#B86B52] transition-colors" data-testid="add-review">+ Add review</button>
            </div>
            <button data-testid="save-reviews" onClick={saveReviews} className="px-8 py-3 bg-[#B86B52] text-white rounded-sm hover:bg-[#a35c45] transition-colors">Save Reviews</button>
          </div>
        )}

        {tab === "curve" && curve && (
          <div className="space-y-8" data-testid="curve-editor">
            <div className="flex items-start justify-between flex-wrap gap-4">
              <p className="text-[#5C5A56] font-light max-w-xl">Each month's multiplier is applied to the base nightly rate (${curve.nightly_rate}). 1.0 = base, 1.2 = +20%, 0.8 = −20%. Holiday premiums override the month for those dates. Prices round to the nearest $5.</p>
              <button onClick={resetCurve} data-testid="reset-curve" className="text-sm text-[#2A4759] hover:text-[#B86B52] transition-colors whitespace-nowrap">↻ Sync to Los Cabos market baseline</button>
            </div>
            <div className="bg-white border border-[#EAE5DC] rounded-sm p-6">
              <div className="overline text-[#5C5A56] mb-4">Monthly Multipliers</div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                {curve.month_factors.map((f, i) => (
                  <div key={i}>
                    <label className="text-sm text-[#1A1918] flex items-center justify-between mb-1">
                      <span>{MONTHS[i]}</span>
                      <span className="text-[#B86B52]">${Math.round(curve.nightly_rate * (Number(f) || 0) / 5) * 5}</span>
                    </label>
                    <input type="number" step="0.05" className={`${inp} w-full`} value={f} onChange={(e) => setMonthFactor(i, e.target.value)} data-testid={`month-factor-${i}`} />
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-white border border-[#EAE5DC] rounded-sm p-6">
              <div className="overline text-[#5C5A56] mb-4">Holiday Premiums (MM-DD ranges, override the month)</div>
              <div className="space-y-3">
                {(curve.holiday_premiums || []).map((h, i) => (
                  <div key={i} className="grid sm:grid-cols-5 gap-3 items-end">
                    <input className={`${inp} w-full`} placeholder="Name" value={h.name} onChange={(e) => setHolidayField(i, "name", e.target.value)} data-testid={`holiday-name-${i}`} />
                    <input className={`${inp} w-full`} placeholder="From MM-DD" value={h.from} onChange={(e) => setHolidayField(i, "from", e.target.value)} data-testid={`holiday-from-${i}`} />
                    <input className={`${inp} w-full`} placeholder="To MM-DD" value={h.to} onChange={(e) => setHolidayField(i, "to", e.target.value)} data-testid={`holiday-to-${i}`} />
                    <div className="flex items-center gap-2">
                      <input type="number" step="0.05" className={`${inp} w-full`} placeholder="Factor" value={h.factor} onChange={(e) => setHolidayField(i, "factor", e.target.value)} data-testid={`holiday-factor-${i}`} />
                      <span className="text-[#B86B52] text-sm whitespace-nowrap">${Math.round(curve.nightly_rate * (Number(h.factor) || 0) / 5) * 5}</span>
                    </div>
                    <button onClick={() => removeHoliday(i)} className="text-[#B86B52] hover:text-[#a35c45] px-2 py-3 justify-self-start" data-testid={`holiday-remove-${i}`}>✕</button>
                  </div>
                ))}
              </div>
              <button onClick={addHoliday} className="mt-4 text-sm text-[#2A4759] hover:text-[#B86B52] transition-colors" data-testid="add-holiday">+ Add holiday premium</button>
            </div>
            <button data-testid="save-curve" onClick={saveCurve} className="px-8 py-3 bg-[#B86B52] text-white rounded-sm hover:bg-[#a35c45] transition-colors">Save Rate Curve</button>
          </div>
        )}

        {tab === "seasons" && seasons && (
          <div className="space-y-5" data-testid="seasons-editor">
            <p className="text-[#5C5A56] font-light max-w-xl">Set premium nightly rates and minimum-stay requirements for peak periods (Christmas, Spring Break, etc.). Any night inside a season uses its rate; leave min-nights at 0 to use the standard {settings.min_nights}-night minimum.</p>
            {seasons.map((s, i) => (
              <div key={i} className="bg-white border border-[#EAE5DC] rounded-sm p-6 grid sm:grid-cols-5 gap-3 items-end">
                <div>
                  <label className="overline text-[#5C5A56] block mb-2">Name</label>
                  <input className={`${inp} w-full`} placeholder="Christmas" value={s.name} onChange={(e) => setSeasonField(i, "name", e.target.value)} data-testid={`season-name-${i}`} />
                </div>
                <div>
                  <label className="overline text-[#5C5A56] block mb-2">Start</label>
                  <input type="date" className={`${inp} w-full`} value={s.start} onChange={(e) => setSeasonField(i, "start", e.target.value)} data-testid={`season-start-${i}`} />
                </div>
                <div>
                  <label className="overline text-[#5C5A56] block mb-2">End</label>
                  <input type="date" className={`${inp} w-full`} value={s.end} onChange={(e) => setSeasonField(i, "end", e.target.value)} data-testid={`season-end-${i}`} />
                </div>
                <div>
                  <label className="overline text-[#5C5A56] block mb-2">Rate ($/nt)</label>
                  <input type="number" className={`${inp} w-full`} value={s.nightly_rate} onChange={(e) => setSeasonField(i, "nightly_rate", e.target.value)} data-testid={`season-rate-${i}`} />
                </div>
                <div className="flex gap-2 items-end">
                  <div className="flex-1">
                    <label className="overline text-[#5C5A56] block mb-2">Min nights</label>
                    <input type="number" className={`${inp} w-full`} value={s.min_nights ?? 0} onChange={(e) => setSeasonField(i, "min_nights", e.target.value)} data-testid={`season-min-${i}`} />
                  </div>
                  <button onClick={() => removeSeason(i)} className="text-[#B86B52] hover:text-[#a35c45] px-2 py-3" data-testid={`season-remove-${i}`}>✕</button>
                </div>
              </div>
            ))}
            <div className="flex gap-4">
              <button onClick={addSeason} className="text-sm text-[#2A4759] hover:text-[#B86B52] transition-colors" data-testid="add-season">+ Add season</button>
            </div>
            <button data-testid="save-seasons" onClick={saveSeasons} className="px-8 py-3 bg-[#B86B52] text-white rounded-sm hover:bg-[#a35c45] transition-colors">Save Seasonal Rates</button>
          </div>
        )}
      </div>
    </div>
  );
}
